import React, { useEffect, useMemo, useState } from 'react';
import { LaborRate } from '../types';
import { inventoryApi } from '../services/api';
import { getIsAdmin } from '../utils/auth';
import { formatCurrency } from '../utils/costing';

interface DraftRate {
    name: string;
    internal: number;
    external: number;
}

const LaborRatesPage: React.FC = () => {
    const isAdmin = getIsAdmin();
    const [serverRates, setServerRates] = useState<LaborRate[]>([]);
    const [draftByRole, setDraftByRole] = useState<Record<string, DraftRate>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const rates = await inventoryApi.listLaborRates();
                setServerRates(rates);
                setDraftByRole(buildDraftMap(rates));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load labor rates');
            } finally {
                setIsLoading(false);
            }
        };
        void load();
    }, []);

    const isDirty = useMemo(
        () =>
            serverRates.some((r) => {
                const draft = draftByRole[r.role];
                if (!draft) return false;
                return (
                    draft.name !== r.name ||
                    draft.internal !== r.internal_rate ||
                    draft.external !== r.external_rate
                );
            }),
        [serverRates, draftByRole]
    );

    const updateDraft = (role: string, patch: Partial<DraftRate>) => {
        setDraftByRole((prev) => ({
            ...prev,
            [role]: { ...prev[role], ...patch },
        }));
    };

    const handleChangeRate = (
        role: string,
        kind: keyof DraftRate,
        value: string
    ) => {
        const parsed = Number(value);
        const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        updateDraft(role, { [kind]: next });
    };

    const handleReset = () => {
        setDraftByRole(buildDraftMap(serverRates));
    };

    const handleResetRow = (rate: LaborRate) => {
        updateDraft(rate.role, {
            name: rate.name,
            internal: rate.internal_rate,
            external: rate.external_rate,
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const payload = serverRates.map((r) => {
                const draft = draftByRole[r.role];
                return {
                    role: r.role,
                    name: draft?.name ?? r.name,
                    internal_rate: draft?.internal ?? r.internal_rate,
                    external_rate: draft?.external ?? r.external_rate,
                };
            });
            const saved = await inventoryApi.updateLaborRates(payload);
            setServerRates(saved);
            setDraftByRole(buildDraftMap(saved));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save labor rates');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="ff-mono text-xs text-[#141414]/55">
                Loading labor rates…
            </div>
        );
    }

    return (
        <div>
            {error && (
                <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-3 py-2 ff-mono text-xs">
                    {error}
                </div>
            )}

            {serverRates.length === 0 ? (
                <div className="ff-mono text-xs text-[#141414]/55">
                    No labor rates configured.
                </div>
            ) : (
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 border-b border-[#141414]/15">
                            <th className="py-2 pr-4 font-normal">Role</th>
                            <th className="py-2 pr-4 font-normal">Internal /hr</th>
                            <th className="py-2 pr-4 font-normal">External /hr</th>
                            {isAdmin && <th className="py-2 font-normal"></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {serverRates.map((rate) => {
                            const draft = draftByRole[rate.role] ?? {
                                name: rate.name,
                                internal: rate.internal_rate,
                                external: rate.external_rate,
                            };
                            const changed =
                                draft.name !== rate.name ||
                                draft.internal !== rate.internal_rate ||
                                draft.external !== rate.external_rate;
                            return (
                                <tr
                                    key={rate.role}
                                    className="border-b border-[#141414]/10 last:border-0"
                                >
                                    <td className="py-2 pr-4">
                                        {isAdmin ? (
                                            <input
                                                type="text"
                                                value={draft.name}
                                                onChange={(e) =>
                                                    updateDraft(rate.role, {
                                                        name: e.target.value,
                                                    })
                                                }
                                                className="w-48 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-sm text-[#141414]"
                                                aria-label={`Name for ${rate.role}`}
                                            />
                                        ) : (
                                            <span className="ff-mono text-sm text-[#141414]">
                                                {rate.name}
                                            </span>
                                        )}
                                        {changed && (
                                            <span className="ml-2 ff-mono text-[10px] uppercase tracking-[0.2em] text-[#b87a1a]">
                                                modified
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2 pr-4">
                                        <RateCell
                                            isAdmin={isAdmin}
                                            value={draft.internal}
                                            ariaLabel={`Internal rate for ${rate.role}`}
                                            onChange={(v) =>
                                                handleChangeRate(rate.role, 'internal', v)
                                            }
                                        />
                                    </td>
                                    <td className="py-2 pr-4">
                                        <RateCell
                                            isAdmin={isAdmin}
                                            value={draft.external}
                                            ariaLabel={`External rate for ${rate.role}`}
                                            onChange={(v) =>
                                                handleChangeRate(rate.role, 'external', v)
                                            }
                                        />
                                    </td>
                                    {isAdmin && (
                                        <td className="py-2 text-right">
                                            {changed && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleResetRow(rate)}
                                                    className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 hover:text-[#e0461e] transition"
                                                >
                                                    reset
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {isAdmin && serverRates.length > 0 && (
                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!isDirty || isSaving}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                    >
                        {isSaving ? 'Saving…' : 'Save labor rates'}
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        disabled={!isDirty || isSaving}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 disabled:opacity-50 transition"
                    >
                        Reset
                    </button>
                </div>
            )}
        </div>
    );
};

interface RateCellProps {
    isAdmin: boolean;
    value: number;
    ariaLabel: string;
    onChange: (raw: string) => void;
}

const RateCell: React.FC<RateCellProps> = ({ isAdmin, value, ariaLabel, onChange }) => {
    if (!isAdmin) {
        return (
            <span className="ff-mono text-xs text-[#141414]">
                {formatCurrency(value)}
                <span className="text-[#141414]/55"> /hr</span>
            </span>
        );
    }
    return (
        <div className="flex items-center gap-2">
            <span className="ff-mono text-[11px] text-[#141414]/55">$</span>
            <input
                type="number"
                min={0}
                step={1}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="num-input w-24 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-sm text-[#141414]"
                aria-label={ariaLabel}
            />
            <span className="ff-mono text-[11px] text-[#141414]/55">/hr</span>
        </div>
    );
};

const buildDraftMap = (rates: LaborRate[]): Record<string, DraftRate> =>
    Object.fromEntries(
        rates.map((r) => [
            r.role,
            {
                name: r.name,
                internal: r.internal_rate,
                external: r.external_rate,
            },
        ])
    );

export default LaborRatesPage;
